import React, { useEffect, useRef, useState } from 'react';
import { Link as RouterLink, useParams } from 'react-router-dom';
import {
  Box, Typography, Button, Paper, TextField, Select, MenuItem, FormControl, InputLabel,
  Checkbox, FormControlLabel, LinearProgress, Alert, Chip, Collapse, Backdrop, CircularProgress, Tabs, Tab,
} from '@mui/material';
import { getProject, updateProject } from '../../api/annotatorProjects';
import { exportProject } from '../../api/annotatorExport';
import {
  CATEGORY_MODEL_MAP, DEFAULT_TRAIN_PARAMS, cancelTraining, deployModel, deployLatestModel, downloadBestModel,
  fetchTrainingConfig, fetchTrainingDevices, fetchTrainingRuns, fetchTrainingRunLog, startTraining, streamTrainingLogs,
} from '../../api/annotatorTraining';
import type { TrainingRunSummary } from '../../api/annotatorTraining';
import DownloadIcon from '@mui/icons-material/Download';
import { getAnnotatorSettings } from '../../api/annotatorSettings';
import type { AnnotatorProject, TrainParams, CategoryInfo, DeployResult, ExportResult, TrainingDevice } from '../../types/annotator';
import { useAuth } from '../../contexts/AuthContext';

interface DmoInferenceModel {
  id: string;
  name: string;
  displayName: string;
  backend: string;
  modelPath: string;
  isActive: boolean;
}

interface DeviceClassFilterChange {
  deviceName: string;
  from: string | null;
  to: string | null;
  removedClasses: string[];
}

type RunStatus = 'idle' | 'running' | 'done' | 'error' | 'cancelled';

function describeFilterChange(change: DeviceClassFilterChange): string {
  const notes = [
    ...(change.removedClasses.length ? [`lost ${change.removedClasses.join(', ')}`] : []),
    ...(change.to === null ? ['now all classes'] : []),
  ];
  return notes.length ? `${change.deviceName} (${notes.join(', ')})` : change.deviceName;
}

function deriveCategoryFromProject(project: AnnotatorProject): string {
  if (project.output_folder) {
    const parts = project.output_folder.replace(/\\/g, '/').split('/');
    const last = parts[parts.length - 1];
    if (last) return last;
  }
  return project.name.toLowerCase().replace(/\s+/g, '_');
}

const STATUS_COLORS: Record<RunStatus, 'default' | 'primary' | 'success' | 'error' | 'warning'> = {
  idle: 'default', running: 'primary', done: 'success', error: 'error', cancelled: 'warning',
};

const HISTORY_STATUS_COLORS: Record<TrainingRunSummary['status'], 'default' | 'primary' | 'success' | 'error' | 'warning'> = {
  running: 'primary', done: 'success', error: 'error', cancelled: 'warning', interrupted: 'warning',
};

function formatRunTime(iso: string | null): string {
  return iso ? new Date(iso).toLocaleString() : '—';
}

export default function AnnotatorTrainingPage() {
  const { projectId } = useParams<{ projectId: string }>();
  const auth = useAuth();
  const accessToken = auth.tokens?.accessToken;
  const [project, setProject] = useState<AnnotatorProject | null>(null);
  const [trainingConfig, setTrainingConfig] = useState<CategoryInfo | null>(null);
  const [params, setParams] = useState<TrainParams>({ ...DEFAULT_TRAIN_PARAMS });
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [globalBackend, setGlobalBackend] = useState('libreyolo');
  const [devices, setDevices] = useState<TrainingDevice[]>([]);

  const [editingFolder, setEditingFolder] = useState(false);
  const [folderValue, setFolderValue] = useState('');
  const [deleteContents, setDeleteContents] = useState(false);
  const [folderSaving, setFolderSaving] = useState(false);
  const [folderError, setFolderError] = useState('');
  const [deletingContents, setDeletingContents] = useState(false);

  const [exportBeforeTraining, setExportBeforeTraining] = useState(true);
  const [includeUnannotated, setIncludeUnannotated] = useState(true);
  const [exportResult, setExportResult] = useState<ExportResult | null>(null);
  const [exportError, setExportError] = useState('');
  const [exportRunning, setExportRunning] = useState(false);

  const [starting, setStarting] = useState(false);
  const [status, setStatus] = useState<RunStatus>('idle');
  const [runId, setRunId] = useState<string | null>(null);
  const [logs, setLogs] = useState<string[]>([]);
  const [currentEpoch, setCurrentEpoch] = useState(0);
  const [totalEpochs, setTotalEpochs] = useState(0);
  const [saveDir, setSaveDir] = useState<string | null>(null);
  const [bestMap50, setBestMap50] = useState<number | null>(null);
  const [bestEpoch, setBestEpoch] = useState<number | null>(null);
  const [formError, setFormError] = useState('');
  const [deployResult, setDeployResult] = useState<{
    deployed_to: string; source: string; archived: string | null; removed_engines: string[];
    classes_updated: boolean; inference_reloaded: boolean; inference_reload_error: string | null;
  } | null>(null);
  const [deployError, setDeployError] = useState('');
  const [downloading, setDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState('');
  const [deploying, setDeploying] = useState(false);

  // DMO model registry state
  const [dmoModels, setDmoModels] = useState<DmoInferenceModel[]>([]);
  const [trainingModelId, setTrainingModelId] = useState('');
  const [registerMode, setRegisterMode] = useState<'update' | 'new'>('update');
  const [selectedDmoModelId, setSelectedDmoModelId] = useState('');
  const [newModelDisplayName, setNewModelDisplayName] = useState('');
  const [registering, setRegistering] = useState(false);
  const [registerResult, setRegisterResult] = useState<string | null>(null);
  const [registerError, setRegisterError] = useState('');

  // Right-column tabs + persisted run history
  const [rightTab, setRightTab] = useState<'deploy' | 'logs'>('deploy');
  const [runs, setRuns] = useState<TrainingRunSummary[]>([]);
  const [viewRunId, setViewRunId] = useState<string>('');
  const [viewedLog, setViewedLog] = useState<(TrainingRunSummary & { lines: string[] }) | null>(null);
  const [viewedLogLoading, setViewedLogLoading] = useState(false);
  const [viewedLogError, setViewedLogError] = useState('');

  const logRef = useRef<HTMLPreElement>(null);
  const abortRef = useRef<AbortController | null>(null);

  useEffect(() => {
    if (!projectId) return;
    getProject(Number(projectId)).then((p) => {
      setProject(p);
      setFolderValue(p.output_folder ?? '');
      setParams((prev) => ({ ...prev, category: deriveCategoryFromProject(p) }));
    }).catch(console.error);
    fetchTrainingConfig(Number(projectId)).then(setTrainingConfig).catch(console.error);
    getAnnotatorSettings().then((s) => {
      const backend = s.training_backend ?? 'libreyolo';
      setGlobalBackend(backend);
      setParams((prev) => ({ ...prev, backend }));
    }).catch(console.error);
    fetchTrainingDevices(Number(projectId)).then(({ devices: list }) => {
      setDevices(list);
      // List is sorted GPUs-by-VRAM-desc then CPU last, so the first entry
      // is "biggest GPU if any, else CPU" — the right default either way.
      if (list.length > 0) setParam('device', list[0].index);
    }).catch(console.error);
  }, [projectId]);

  useEffect(() => {
    fetch('/api/inference-models')
      .then((r) => r.ok ? r.json() : Promise.reject())
      .then((data: DmoInferenceModel[]) => {
        const active = data.filter((m) => m.isActive);
        setDmoModels(active);
        if (active.length > 0) setSelectedDmoModelId(active[0].id);
      })
      .catch(() => {});
  }, []);

  // Pre-select the project's assigned inference model once both are loaded
  useEffect(() => {
    if (!project || dmoModels.length === 0) return;
    if (project.inference_model_id) {
      const match = dmoModels.find((m) => m.id === project.inference_model_id);
      if (match) {
        setTrainingModelId(match.id);
        setSelectedDmoModelId(match.id);
        setParams((prev) => ({ ...prev, category: match.name }));
      }
    }
  }, [project, dmoModels]);

  useEffect(() => {
    if (!projectId) return;
    fetchTrainingRuns(Number(projectId)).then((list) => {
      setRuns(list);
      const latest = list[0];
      if (!latest) return;
      setViewRunId(latest.run_id);
      // A run still going on the server (e.g. browser was closed mid-training): pick it back up.
      if (latest.status === 'running') {
        setParams((prev) => ({ ...prev, category: latest.category }));
        setRightTab('logs');
        attachToRun(latest.run_id, 0);
      }
    }).catch(console.error);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  // Load the stored log when viewing a run other than the live one
  useEffect(() => {
    if (!projectId || !viewRunId || viewRunId === runId) { setViewedLog(null); return; }
    setViewedLogLoading(true); setViewedLogError('');
    fetchTrainingRunLog(Number(projectId), viewRunId)
      .then(setViewedLog)
      .catch((err: unknown) => { setViewedLog(null); setViewedLogError(err instanceof Error ? err.message : String(err)); })
      .finally(() => setViewedLogLoading(false));
  }, [projectId, viewRunId, runId]);

  function refreshRuns() {
    if (!projectId) return;
    fetchTrainingRuns(Number(projectId)).then(setRuns).catch(console.error);
  }

  const viewingLive = !!viewRunId && viewRunId === runId;
  const shownLines = viewingLive ? logs : viewedLog?.lines ?? [];

  useEffect(() => { if (logRef.current) logRef.current.scrollTop = logRef.current.scrollHeight; }, [shownLines, rightTab]);
  useEffect(() => () => { abortRef.current?.abort(); }, []);

  function setParam<K extends keyof TrainParams>(key: K, value: TrainParams[K]) {
    setParams((prev) => ({ ...prev, [key]: value }));
  }

  async function handleSaveFolder() {
    if (!project) return;
    setFolderError(''); setFolderSaving(true);
    try {
      const updated = await updateProject(project.id, { output_folder: folderValue.trim() });
      setProject(updated); setFolderValue(updated.output_folder ?? ''); setEditingFolder(false);
    } catch (err: any) {
      setFolderError(err?.response?.data?.detail ?? 'Failed to update output folder.');
    } finally { setFolderSaving(false); }
  }

  async function handleDeleteContents() {
    if (!project) return;
    setFolderError(''); setDeletingContents(true);
    try {
      const updated = await updateProject(project.id, { delete_output_folder: true });
      setProject(updated); setDeleteContents(false);
    } catch (err: any) {
      setFolderError(err?.response?.data?.detail ?? 'Failed to delete folder contents.');
    } finally { setDeletingContents(false); }
  }

  async function handleStart(e: React.FormEvent) {
    e.preventDefault();
    if (!projectId) return;
    setFormError(''); setExportError(''); setExportResult(null); setLogs([]); setCurrentEpoch(0);
    setTotalEpochs(params.epochs); setSaveDir(null); setBestMap50(null); setBestEpoch(null); setDeployResult(null); setDeployError('');
    setStarting(true);

    if (exportBeforeTraining) {
      setExportRunning(true);
      try {
        const res = await exportProject(Number(projectId), includeUnannotated, true);
        setExportResult(res);
      } catch (err: any) {
        setExportError(err?.response?.data?.detail ?? err?.message ?? 'Export failed.');
        setExportRunning(false);
        setStarting(false);
        return;
      }
      setExportRunning(false);
    }

    try {
      const { run_id } = await startTraining(Number(projectId), params);
      setViewRunId(run_id);
      setRightTab('logs');
      attachToRun(run_id, params.epochs);
      refreshRuns();
    } catch (err: unknown) {
      setStarting(false);
      setFormError(err instanceof Error ? err.message : String(err));
    }
  }

  // Stream a run's log (the server replays it from the first line), used both for a
  // freshly started run and for re-attaching to one that was running when the page loaded.
  function attachToRun(run_id: string, fallbackEpochs: number) {
    if (!projectId) return;
    abortRef.current?.abort();
    setRunId(run_id); setStatus('running'); setLogs([]);
    let firstLogSeen = false;
    abortRef.current = streamTrainingLogs(
      Number(projectId), run_id,
      (msg) => {
        if (msg.type === 'log') {
          if (!firstLogSeen) { firstLogSeen = true; setStarting(false); }
          setLogs((prev) => [...prev, (msg.line as string) ?? '']);
          if (msg.epoch != null) { setCurrentEpoch(msg.epoch as number); setTotalEpochs((msg.total_epochs as number) || fallbackEpochs); }
        } else if (msg.type === 'done') {
          setStarting(false);
          setStatus('done');
          setSaveDir((msg.save_dir as string) ?? null);
          if (msg.best_map50 != null) setBestMap50(msg.best_map50 as number);
          if (msg.best_epoch != null) setBestEpoch(msg.best_epoch as number);
          refreshRuns();
        }
        else if (msg.type === 'error') { setStarting(false); setStatus('error'); refreshRuns(); }
        else if (msg.type === 'cancelled') { setStarting(false); setStatus('cancelled'); refreshRuns(); }
      },
      () => setStatus((prev) => prev === 'running' ? 'done' : prev),
      (err) => { setStarting(false); setFormError(`Stream error: ${err}`); setStatus('error'); },
    );
  }

  async function handleCancel() {
    if (!projectId || !runId) return;
    abortRef.current?.abort();
    await cancelTraining(Number(projectId), runId).catch(() => {});
    setStatus('cancelled');
    refreshRuns();
  }

  async function handleDeploy() {
    if (!projectId || !runId) return;
    setDeployError(''); setDeploying(true); setRegisterResult(null); setRegisterError('');
    try {
      const r = await deployModel(Number(projectId), runId, params.category, dmoDeployPath ?? undefined);
      setDeployResult(r);
      await syncRegisteredModel(r);
    } catch (err: unknown) {
      setDeployError(err instanceof Error ? err.message : String(err));
    } finally { setDeploying(false); }
  }

  async function handleDeployLatest() {
    if (!projectId) return;
    setDeployError(''); setDeploying(true); setDeployResult(null);
    try {
      const r = await deployLatestModel(Number(projectId), params.category, dmoDeployPath ?? undefined);
      setDeployResult(r);
      await syncRegisteredModel(r);
    } catch (err: unknown) {
      setDeployError(err instanceof Error ? err.message : String(err));
    } finally { setDeploying(false); }
  }

  async function handleDownloadBest() {
    if (!projectId) return;
    setDownloadError(''); setDownloading(true);
    try {
      await downloadBestModel(Number(projectId), params.category.trim() || undefined);
    } catch (err: unknown) {
      setDownloadError(err instanceof Error ? err.message : String(err));
    } finally { setDownloading(false); }
  }

  function handleSelectTrainingModel(modelId: string) {
    setTrainingModelId(modelId);
    if (modelId) {
      const m = dmoModels.find((dm) => dm.id === modelId);
      if (m) {
        setParam('category', m.name);
        setSelectedDmoModelId(modelId);
      }
    } else {
      if (project) setParam('category', deriveCategoryFromProject(project));
      setSelectedDmoModelId('');
    }
  }

  // Deploying over a registered model puts its new class order live at once, so
  // the registry and device filters follow now, not whenever Update Model is clicked.
  async function syncRegisteredModel(deployed: DeployResult) {
    if (selectedDmoModel) await handleRegister(deployed, 'update', selectedDmoModel.id);
  }

  async function handleRegister(deployed = deployResult, mode = registerMode, modelId = selectedDmoModelId) {
    if (!deployed || !project) return;
    setRegistering(true); setRegisterError(''); setRegisterResult(null);
    const modelPath = deployed.deployed_to.replace('/app/inference_models/', 'models/');
    const now = new Date().toISOString();
    try {
      if (mode === 'update') {
        if (!modelId) { setRegisterError('Select a model to update.'); setRegistering(false); return; }
        const existing = dmoModels.find((m) => m.id === modelId);
        if (!existing) { setRegisterError('Selected model not found.'); setRegistering(false); return; }
        const classes = [...project.labels]
          .sort((a, b) => a.class_index - b.class_index)
          .map((l) => ({ classIndex: l.class_index, className: l.name }));
        const res = await fetch(`/api/inference-models/${modelId}`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${accessToken}` },
          body: JSON.stringify({
            displayName: existing.displayName,
            backend: existing.backend,
            modelPath,
            isActive: true,
            trainedFromProjectId: project.id,
            lastTrainedAt: now,
            classes,
          }),
        });
        if (!res.ok) throw new Error(`API returned ${res.status}`);
        const { deviceFilterChanges } = (await res.json()) as { deviceFilterChanges: DeviceClassFilterChange[] };
        const moved = deviceFilterChanges.map(describeFilterChange);
        setRegisterResult(
          `Updated "${existing.displayName}" in DMO model registry.`
          + (moved.length ? ` Device class filters moved to the new numbering: ${moved.join('; ')}.` : ''),
        );
      } else {
        const name = params.category.trim().toLowerCase().replace(/\s+/g, '_');
        const classes = [...project.labels]
          .sort((a, b) => a.class_index - b.class_index)
          .map((l) => ({ classIndex: l.class_index, className: l.name }));
        const res = await fetch('/api/inference-models', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${accessToken}` },
          body: JSON.stringify({
            name,
            displayName: newModelDisplayName.trim() || name,
            backend: params.backend ?? 'libreyolo',
            modelPath,
            classes,
            trainedFromProjectId: project.id,
            lastTrainedAt: now,
          }),
        });
        if (!res.ok) {
          const text = await res.text();
          throw new Error(text || `API returned ${res.status}`);
        }
        setRegisterResult(`Registered new model "${newModelDisplayName.trim() || name}" in DMO model registry.`);
        const data = await fetch('/api/inference-models').then((r) => r.json());
        setDmoModels((data as DmoInferenceModel[]).filter((m) => m.isActive));
      }
    } catch (err: any) {
      setRegisterError(err.message || 'Registration failed.');
    } finally {
      setRegistering(false);
    }
  }

  const inferenceMapping = CATEGORY_MODEL_MAP[params.category];
  const selectedDmoModel = dmoModels.find((m) => m.id === trainingModelId) ?? null;
  // Strip "models/" prefix to get path relative to inference_models_path, e.g. libreyolo/fox_cat_pig/best.pt
  const dmoDeployPath = selectedDmoModel ? selectedDmoModel.modelPath.replace(/^models\//, '') : null;
  const canDeploy = !!inferenceMapping || !!selectedDmoModel;
  const resolvedDeployPath = trainingConfig?.resolved_targets?.[params.category] ?? null;
  const epochPercent = totalEpochs > 0 ? Math.round((currentEpoch / totalEpochs) * 100) : 0;
  const annotatedCount = project?.annotated_count ?? 0;
  const unverifiedCount = project?.unverified_count ?? 0;
  const unannotatedCount = (project?.image_count ?? 0) - annotatedCount;
  const verifiedImageCount = (project?.image_count ?? 0) - unverifiedCount;
  const exportCount = includeUnannotated ? verifiedImageCount : annotatedCount;

  if (!project) return <Typography color="text.secondary">Loading…</Typography>;

  return (
    <Box>
      <Box sx={{ mb: 3 }}>
        <Button component={RouterLink} to={`/annotator/projects/${projectId}`} size="small" sx={{ mb: 0.5 }}>← {project.name}</Button>
        <Typography variant="h5" fontWeight={600}>Train Model</Typography>
      </Box>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' }, gap: 3 }}>
        {/* Parameters form */}
        <Paper sx={{ p: 3 }}>
          <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>Parameters</Typography>

          {(inferenceMapping || selectedDmoModel) && (
            <Alert severity="info" sx={{ mb: 2 }}>
              Deploys to: <code>
                {selectedDmoModel
                  ? selectedDmoModel.modelPath
                  : resolvedDeployPath ?? `${inferenceMapping!.dir}/${inferenceMapping!.file}`}
              </code>
            </Alert>
          )}
          {params.category && !inferenceMapping && !selectedDmoModel && (
            <Alert severity="warning" sx={{ mb: 2 }}>No inference mapping for "{params.category}"</Alert>
          )}

          {/* Output folder */}
          <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Typography variant="subtitle2">Output Folder</Typography>
              <Button size="small" onClick={() => { setEditingFolder((v) => !v); setFolderValue(project?.output_folder ?? ''); setFolderError(''); }}>
                {editingFolder ? 'Cancel' : 'Edit'}
              </Button>
            </Box>
            {editingFolder ? (
              <Box>
                <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                  <TextField size="small" fullWidth value={folderValue} onChange={(e) => setFolderValue(e.target.value)} sx={{ '& input': { fontFamily: 'monospace', fontSize: 12 } }} />
                  <Button variant="contained" size="small" onClick={handleSaveFolder} disabled={folderSaving}>{folderSaving ? 'Saving…' : 'Save'}</Button>
                </Box>
                {folderError && <Alert severity="error" sx={{ mt: 1 }}>{folderError}</Alert>}
              </Box>
            ) : (
              <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: 12 }}>{project?.output_folder || <em>not set</em>}</Typography>
            )}
            <FormControlLabel
              control={<Checkbox size="small" checked={deleteContents} onChange={(e) => setDeleteContents(e.target.checked)} disabled={deletingContents || !project?.output_folder} />}
              label={<Typography variant="body2">Delete child directories before export</Typography>}
              sx={{ mt: 1 }}
            />
            {deleteContents && (
              <Box sx={{ ml: 4, mt: 0.5 }}>
                <Typography variant="caption" color="text.secondary">Deletes images/ and labels/ subdirectories.</Typography>
                <Button size="small" color="error" variant="outlined" onClick={handleDeleteContents} disabled={deletingContents} sx={{ ml: 1, mt: 0.5 }}>
                  {deletingContents ? 'Deleting…' : 'Delete Now'}
                </Button>
              </Box>
            )}
          </Paper>

          {/* Export before training */}
          <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
            <FormControlLabel
              control={<Checkbox size="small" checked={exportBeforeTraining} onChange={(e) => setExportBeforeTraining(e.target.checked)} disabled={status === 'running'} />}
              label={<Typography variant="body2" fontWeight={600}>Export dataset before training</Typography>}
            />
            <Collapse in={exportBeforeTraining}>
              <Box sx={{ ml: 4, mt: 0.5 }}>
                <FormControlLabel
                  control={<Checkbox size="small" checked={includeUnannotated} onChange={(e) => setIncludeUnannotated(e.target.checked)} disabled={status === 'running'} />}
                  label={<Typography variant="body2">Include unannotated images as negative samples</Typography>}
                />
                <Typography variant="caption" color="text.secondary" display="block">
                  {annotatedCount} annotated · {unannotatedCount} unannotated
                  {unverifiedCount > 0 ? ` · ${unverifiedCount} unverified (excluded)` : ''}
                  {' · '}{exportCount} will be exported
                </Typography>
                {exportError && <Alert severity="error" sx={{ mt: 0.5 }}>{exportError}</Alert>}
                {exportRunning && <Typography variant="caption" color="text.secondary">Exporting…</Typography>}
                {exportResult && !exportRunning && (
                  <Alert severity="success" sx={{ mt: 0.5 }}>
                    Exported {exportResult.total_images} image(s) to {exportResult.export_path}
                    {exportResult.skipped_unannotated > 0 ? ` (${exportResult.skipped_unannotated} skipped)` : ''}
                  </Alert>
                )}
              </Box>
            </Collapse>
          </Paper>

          <form onSubmit={handleStart}>
            {dmoModels.length > 0 && (
              <Box sx={{ mb: 2 }}>
                <FormControl size="small" fullWidth>
                  <InputLabel>DMO Inference Model</InputLabel>
                  <Select
                    label="DMO Inference Model"
                    value={trainingModelId}
                    onChange={(e) => handleSelectTrainingModel(e.target.value)}
                    disabled={status === 'running'}
                  >
                    <MenuItem value=""><em>None — use category field directly</em></MenuItem>
                    {dmoModels.map((m) => (
                      <MenuItem key={m.id} value={m.id}>{m.displayName} ({m.name})</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Typography variant="caption" color="text.secondary">
                  Selecting a model auto-fills Category and pre-selects it in the register step.
                </Typography>
              </Box>
            )}

            <FormControlLabel
              control={<Checkbox size="small" checked={params.resume} onChange={(e) => {
                const isResume = e.target.checked;
                setParams((p) => ({
                  ...p,
                  resume: isResume,
                  epochs: isResume ? 10 : 130,
                  lr: isResume ? 0.001 : 0.01,
                }));
              }} disabled={status === 'running'} />}
              label={<Typography variant="body2" fontWeight={600}>Resume training from last checkpoint</Typography>}
              sx={{ mb: 1 }}
            />
            {params.resume && (
              <Typography variant="caption" color="text.secondary" display="block" sx={{ ml: 4, mb: 1 }}>
                Continues from the last best.pt with a lower learning rate. Good for adding new images without full retraining.
              </Typography>
            )}

            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(130px, 1fr))', gap: 1.5, mb: 2 }}>
              <TextField size="small" label="Category" value={params.category} onChange={(e) => setParam('category', e.target.value)} required disabled={status === 'running'} />
              <FormControl size="small">
                <InputLabel>Backend</InputLabel>
                <Select value={params.backend ?? globalBackend} label="Backend" onChange={(e) => setParam('backend', e.target.value)} disabled={status === 'running'}>
                  <MenuItem value="libreyolo">libreyolo</MenuItem>
                </Select>
              </FormControl>
              <FormControl size="small">
                <InputLabel>Model Size</InputLabel>
                <Select value={params.model_size} label="Model Size" onChange={(e) => setParam('model_size', e.target.value)} disabled={status === 'running'}>
                  <MenuItem value="n">n — nano</MenuItem>
                  <MenuItem value="s">s — small</MenuItem>
                  <MenuItem value="m">m — medium</MenuItem>
                  <MenuItem value="l">l — large</MenuItem>
                  <MenuItem value="x">x — xlarge</MenuItem>
                </Select>
              </FormControl>
              <TextField size="small" label="Epochs" type="number" inputProps={{ min: 1, max: 1000 }} value={params.epochs} onChange={(e) => setParam('epochs', Number(e.target.value))} disabled={status === 'running'} />
              <TextField size="small" label="Learning Rate" type="number" inputProps={{ min: 0.0001, max: 0.1, step: 0.0001 }} value={params.lr} onChange={(e) => setParam('lr', Number(e.target.value))} disabled={status === 'running'} />
              <TextField size="small" label="Batch" type="number" inputProps={{ min: 1, max: 256 }} value={params.batch} onChange={(e) => setParam('batch', Number(e.target.value))} disabled={status === 'running'} />
              <TextField size="small" label="Img Size" type="number" inputProps={{ min: 320, max: 1280, step: 32 }} value={params.image_size} onChange={(e) => setParam('image_size', Number(e.target.value))} disabled={status === 'running'} />
              <FormControl size="small">
                <InputLabel>Device</InputLabel>
                <Select value={params.device} label="Device" onChange={(e) => setParam('device', e.target.value)} disabled={status === 'running'}>
                  {devices.map((d) => (
                    <MenuItem key={d.index} value={d.index}>{d.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField size="small" label="Val Split" type="number" inputProps={{ min: 0.05, max: 0.5, step: 0.05 }} value={params.val_split} onChange={(e) => setParam('val_split', Number(e.target.value))} disabled={status === 'running'} />
              <TextField size="small" label="Patience" type="number" inputProps={{ min: 1, max: 500 }} value={params.patience} onChange={(e) => setParam('patience', Number(e.target.value))} disabled={status === 'running'} />
            </Box>

            <Button size="small" onClick={() => setShowAdvanced((v) => !v)} sx={{ mb: 1 }}>
              {showAdvanced ? '▲ Hide Advanced' : '▼ Show Advanced Augmentation'}
            </Button>

            <Collapse in={showAdvanced}>
              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(130px, 1fr))', gap: 1.5, mb: 2 }}>
                <TextField size="small" label="CLS Loss" type="number" inputProps={{ step: 0.1, min: 0.1, max: 10 }} value={params.cls} onChange={(e) => setParam('cls', Number(e.target.value))} disabled={status === 'running'} />
                <TextField size="small" label="Mosaic" type="number" inputProps={{ step: 0.05, min: 0, max: 1 }} value={params.mosaic} onChange={(e) => setParam('mosaic', Number(e.target.value))} disabled={status === 'running'} />
                <TextField size="small" label="Mixup" type="number" inputProps={{ step: 0.01, min: 0, max: 1 }} value={params.mixup} onChange={(e) => setParam('mixup', Number(e.target.value))} disabled={status === 'running'} />
                <TextField size="small" label="Copy-Paste" type="number" inputProps={{ step: 0.05, min: 0, max: 1 }} value={params.copy_paste} onChange={(e) => setParam('copy_paste', Number(e.target.value))} disabled={status === 'running'} />
                <TextField size="small" label="HSV Sat" type="number" inputProps={{ step: 0.05, min: 0, max: 1 }} value={params.hsv_s} onChange={(e) => setParam('hsv_s', Number(e.target.value))} disabled={status === 'running'} />
                <TextField size="small" label="Erasing" type="number" inputProps={{ step: 0.05, min: 0, max: 1 }} value={params.erasing} onChange={(e) => setParam('erasing', Number(e.target.value))} disabled={status === 'running'} />
                <TextField size="small" label="No-Aug Epochs" type="number" inputProps={{ min: 0, max: params.epochs }} value={params.no_aug_epochs} onChange={(e) => setParam('no_aug_epochs', Number(e.target.value))} disabled={status === 'running'} />
                <TextField size="small" label="Label Override" value={params.label} onChange={(e) => setParam('label', e.target.value)} placeholder="e.g. pig" disabled={status === 'running'} sx={{ gridColumn: 'span 2' }} />
              </Box>
            </Collapse>

            {formError && <Alert severity="error" sx={{ mb: 2 }}>{formError}</Alert>}

            <Box sx={{ display: 'flex', gap: 1 }}>
              {status !== 'running' ? (
                <Button type="submit" variant="contained" disabled={!params.category.trim() || starting}>
                  {starting ? 'Starting…' : 'Start Training'}
                </Button>
              ) : (
                <Button variant="contained" color="error" onClick={handleCancel}>Cancel</Button>
              )}
            </Box>
          </form>
        </Paper>

        <Backdrop
          open={starting}
          sx={{
            position: 'fixed', zIndex: (theme) => theme.zIndex.drawer + 10,
            color: '#fff', bgcolor: 'rgba(255, 255, 255, 0.4)',
          }}
        >
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
            <CircularProgress />
            <Typography variant="body2" color="text.primary">Starting training…</Typography>
          </Box>
        </Backdrop>

        {/* Progress + tabs: Deploy / Training Logs */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {status !== 'idle' && (
            <Paper sx={{ p: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Chip label={status} color={STATUS_COLORS[status]} size="small" />
                {status === 'running' && totalEpochs > 0 && (
                  <Typography variant="body2" color="text.secondary">{currentEpoch} / {totalEpochs} epochs</Typography>
                )}
              </Box>
              {status === 'running' && totalEpochs > 0 && <LinearProgress variant="determinate" value={epochPercent} sx={{ height: 6, borderRadius: 3 }} />}
              {status === 'done' && saveDir && (
                <Typography variant="caption" sx={{ fontFamily: 'monospace', wordBreak: 'break-all', mt: 1, display: 'block' }}>
                  Output: {saveDir}
                </Typography>
              )}
              {status === 'done' && (bestMap50 != null || bestEpoch != null) && (
                <Box sx={{ display: 'flex', gap: 3, mt: 1 }}>
                  {bestMap50 != null && (
                    <Typography variant="body2">
                      <strong>Best mAP50:</strong> {bestMap50.toFixed(3)}
                    </Typography>
                  )}
                  {bestEpoch != null && (
                    <Typography variant="body2">
                      <strong>Best epoch:</strong> {bestEpoch}
                    </Typography>
                  )}
                </Box>
              )}
            </Paper>
          )}

          <Paper sx={{ px: 1 }}>
            <Tabs value={rightTab} onChange={(_, v) => setRightTab(v)}>
              <Tab value="deploy" label="Deploy" />
              <Tab value="logs" label={`Training Logs${runs.length ? ` (${runs.length})` : ''}`} />
            </Tabs>
          </Paper>

          {rightTab === 'deploy' && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {status === 'done' && canDeploy && (
                <Paper sx={{ p: 2 }}>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>Deploy to Inference</Typography>
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1, fontFamily: 'monospace', wordBreak: 'break-all' }}>
                    Copy best.pt to: {selectedDmoModel ? selectedDmoModel.modelPath : resolvedDeployPath ?? (inferenceMapping ? `${inferenceMapping.dir}/${inferenceMapping.file}` : '')}
                  </Typography>
                  {deployResult ? (
                    <Box>
                      <Alert severity="success" sx={{ mb: 1 }}>Deployed to {deployResult.deployed_to}</Alert>
                      {deployResult.archived && <Typography variant="caption" color="text.secondary" display="block">Archived: {deployResult.archived}</Typography>}
                      {deployResult.removed_engines.length > 0 && <Typography variant="caption" color="text.secondary" display="block">Removed stale engines: {deployResult.removed_engines.map(e => e.split('/').pop()).join(', ')}</Typography>}
                      {deployResult.classes_updated && <Typography variant="caption" color="text.secondary" display="block">Updated models_config.json classes</Typography>}
                      {deployResult.inference_reloaded ? (
                        <Typography variant="caption" color="success.main" display="block">Inference service reloaded</Typography>
                      ) : deployResult.inference_reload_error ? (
                        <Typography variant="caption" color="error.main" display="block">Reload failed: {deployResult.inference_reload_error}</Typography>
                      ) : null}
                    </Box>
                  ) : (
                    <Box>
                      {deployError && <Alert severity="error" sx={{ mb: 1 }}>{deployError}</Alert>}
                      <Button variant="contained" onClick={handleDeploy} disabled={deploying}>
                        {deploying ? 'Deploying…' : 'Deploy Model'}
                      </Button>
                    </Box>
                  )}
                </Paper>
              )}

              {/* DMO Model Registry registration — shown after a successful deploy */}
              {deployResult && (
                <Paper sx={{ p: 2 }}>
                  <Typography variant="subtitle2" sx={{ mb: 1.5 }}>Register in DMO Model Registry</Typography>
                  {registerResult ? (
                    <Alert severity="success">{registerResult}</Alert>
                  ) : (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                      <FormControl size="small" fullWidth>
                        <InputLabel>Action</InputLabel>
                        <Select
                          label="Action"
                          value={registerMode}
                          onChange={(e) => setRegisterMode(e.target.value as 'update' | 'new')}
                        >
                          <MenuItem value="update">Update existing model</MenuItem>
                          <MenuItem value="new">Register as new model</MenuItem>
                        </Select>
                      </FormControl>
                      {registerMode === 'update' ? (
                        <FormControl size="small" fullWidth>
                          <InputLabel>Model to update</InputLabel>
                          <Select
                            label="Model to update"
                            value={selectedDmoModelId}
                            onChange={(e) => setSelectedDmoModelId(e.target.value)}
                          >
                            {dmoModels.map((m) => (
                              <MenuItem key={m.id} value={m.id}>{m.displayName} ({m.name})</MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      ) : (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                          <TextField size="small" label="Name (key)" value={params.category} disabled fullWidth />
                          <TextField
                            size="small"
                            label="Display name"
                            value={newModelDisplayName}
                            onChange={(e) => setNewModelDisplayName(e.target.value)}
                            placeholder={params.category}
                            fullWidth
                          />
                        </Box>
                      )}
                      {registerError && <Alert severity="error">{registerError}</Alert>}
                      <Button
                        variant="contained"
                        size="small"
                        onClick={() => handleRegister()}
                        disabled={registering || (registerMode === 'update' && !selectedDmoModelId)}
                        sx={{ alignSelf: 'flex-start' }}
                      >
                        {registering ? 'Registering…' : registerMode === 'update' ? 'Update Model' : 'Register New Model'}
                      </Button>
                    </Box>
                  )}
                </Paper>
              )}

              {/* Deploy latest model – always available when there's a trained model */}
              {canDeploy && status !== 'done' && (
                <Paper sx={{ p: 2 }}>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>Deploy Latest Model</Typography>
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1, fontFamily: 'monospace', wordBreak: 'break-all' }}>
                    Find latest best.pt for {params.category} → {selectedDmoModel ? selectedDmoModel.modelPath : resolvedDeployPath ?? (inferenceMapping ? `${inferenceMapping.dir}/${inferenceMapping.file}` : '')}
                  </Typography>
                  {deployResult ? (
                    <Box>
                      <Alert severity="success" sx={{ mb: 1 }}>Deployed to {deployResult.deployed_to}</Alert>
                      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>Source: {deployResult.source}</Typography>
                      {deployResult.archived && <Typography variant="caption" color="text.secondary" display="block">Archived: {deployResult.archived}</Typography>}
                      {deployResult.removed_engines.length > 0 && <Typography variant="caption" color="text.secondary" display="block">Removed stale engines: {deployResult.removed_engines.map(e => e.split('/').pop()).join(', ')}</Typography>}
                      {deployResult.classes_updated && <Typography variant="caption" color="text.secondary" display="block">Updated models_config.json classes</Typography>}
                      {deployResult.inference_reloaded ? (
                        <Typography variant="caption" color="success.main" display="block">Inference service reloaded</Typography>
                      ) : deployResult.inference_reload_error ? (
                        <Typography variant="caption" color="error.main" display="block">Reload failed: {deployResult.inference_reload_error}</Typography>
                      ) : null}
                      <Button size="small" sx={{ mt: 1 }} onClick={() => setDeployResult(null)}>Dismiss</Button>
                    </Box>
                  ) : (
                    <Box>
                      {deployError && <Alert severity="error" sx={{ mb: 1 }}>{deployError}</Alert>}
                      <Button variant="outlined" onClick={handleDeployLatest} disabled={deploying || status === 'running'}>
                        {deploying ? 'Deploying…' : 'Deploy Latest Model'}
                      </Button>
                    </Box>
                  )}
                </Paper>
              )}

              {/* Download best.pt as a zip — independent of any inference mapping */}
              {params.category.trim() && (
                <Paper sx={{ p: 2 }}>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>Download Model</Typography>
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                    Latest best.pt for {params.category}, zipped.
                  </Typography>
                  {downloadError && <Alert severity="error" sx={{ mb: 1 }}>{downloadError}</Alert>}
                  <Button
                    variant="outlined"
                    startIcon={<DownloadIcon />}
                    onClick={handleDownloadBest}
                    disabled={downloading || status === 'running'}
                  >
                    {downloading ? 'Preparing…' : 'Download best.pt'}
                  </Button>
                </Paper>
              )}
            </Box>
          )}

          {rightTab === 'logs' && (
              <Paper sx={{ p: 2 }}>
                {runs.length === 0 && !runId ? (
                  <Typography variant="body2" color="text.secondary">
                    No training runs recorded for this project yet. Runs started from now on keep their log here,
                    so you can come back to them after closing the browser.
                  </Typography>
                ) : (
                  <>
                    <FormControl size="small" fullWidth sx={{ mb: 1.5 }}>
                      <InputLabel>Training run</InputLabel>
                      <Select label="Training run" value={viewRunId} onChange={(e) => setViewRunId(e.target.value)}>
                        {runs.map((r) => (
                          <MenuItem key={r.run_id} value={r.run_id}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                              <Chip label={r.status} color={HISTORY_STATUS_COLORS[r.status]} size="small" />
                              <span>{formatRunTime(r.started_at)}</span>
                              <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>
                                {r.label || r.category}{r.best_map50 != null ? ` · mAP50 ${r.best_map50.toFixed(3)}` : ''}
                              </Typography>
                            </Box>
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    {!viewingLive && viewedLog && (
                      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                        {viewedLog.category} · started {formatRunTime(viewedLog.started_at)} · finished {formatRunTime(viewedLog.finished_at)}
                        {viewedLog.best_epoch != null ? ` · best epoch ${viewedLog.best_epoch}` : ''}
                        {viewedLog.status === 'interrupted' ? ' · the annotator service restarted before this run finished' : ''}
                        {viewedLog.source === 'output' ? ' · libreyolo log from the output folder (recorded before full run logs were kept)' : ''}
                      </Typography>
                    )}
                    {viewedLogError && <Alert severity="error" sx={{ mb: 1 }}>{viewedLogError}</Alert>}
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography variant="subtitle2">{viewingLive && status === 'running' ? 'Live log' : 'Log'}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {viewedLogLoading ? 'Loading…' : `${shownLines.length} lines`}
                      </Typography>
                    </Box>
                    <Box
                      component="pre"
                      ref={logRef}
                      sx={{
                        fontFamily: 'monospace', fontSize: 11, height: 500, overflow: 'auto',
                        bgcolor: 'background.default', p: 1.5, borderRadius: 1, m: 0, whiteSpace: 'pre-wrap',
                      }}
                    >
                      {shownLines.join('\n')}
                    </Box>
                  </>
                )}
              </Paper>
          )}
        </Box>
      </Box>
    </Box>
  );
}
